import { sortBy } from "lodash";
import { profileHash } from "PFCore/helpers/profile_hash";
import { CustomValue, ProfileExport } from "PFTypes";
import { capitalize, humanize } from "underscore.string";

import { limitLines, limitText, sortInternalExperiences } from "../../../helpers";
import {
  ContactInformationItem,
  CustomType,
  Position,
  PptxgenJsTextOptions,
  ProfileExportFormatted,
  TextItem
} from "../../../types";
import { EY_CERTIFICATES_FIELD_NAMES } from "../../constants";
import { defaultCompanyName, EMPTY_LINE } from "../constants";
import { sidebarHeaderOptions, textOptions } from "../use_ey_pptx_options";
import { getSectors } from "./get_sectors";

// adjusted to fit inteenal experiences indent workaround
const internalExperienceDescriptionIndent = 22;

export const formatExperienceText = (
  formatDate: (str: string) => string,
  jobTitle?: string,
  startDate?: string,
  endDate?: string
) => {
  const jobTitleFormatted = jobTitle ? jobTitle : "";
  const startDateFormatted = startDate ? formatDate(startDate) : "";
  const endDateFormatted = startDate && endDate ? formatDate(endDate) : "";
  const startDateDelimiter = jobTitle && startDate ? " - " : "";
  const endDateDelimiter = startDate && endDate ? " - " : "";

  return [jobTitleFormatted, startDateDelimiter, startDateFormatted, endDateDelimiter, endDateFormatted].join(
    ""
  );
};

export const filterDeselectedSkills = (skills: CustomValue[], skillsType: CustomType) =>
  skills.filter(({ text }) =>
    skillsType.text
      .split("\r")
      .map((text) => text.trim())
      .includes(text)
  );

export const createCustomFieldContent = (
  customTypes: CustomType[],
  names: string[] = [],
  options: PptxgenJsTextOptions
): TextItem[] => {
  const values = names
    .reduce((acc, name) => {
      const customType = customTypes.find(({ type }) => type.name === name);
      if (customType) {
        const vals = customType.text ? customType.text.split("\r ") : [];
        return [...acc, ...vals];
      }
      return acc;
    }, [])
    .sort((str1, str2) => str1.toLowerCase().localeCompare(str2.toLowerCase()));

  const limitedFields = limitText(values.join("\n"), { ...options });

  if (values.length === 0) {
    return [];
  }

  return limitedFields.map((item) => ({
    text: item.text,
    options: {
      breakLine: true,
      bullet: { indent: 9 }
    }
  }));
};

export const createCustomField = (
  customTypes: CustomType[],
  names: string[],
  title: string,
  options: PptxgenJsTextOptions
): TextItem[] => {
  const content = createCustomFieldContent(customTypes, names, options);
  if (!content.length) {
    return [];
  }

  return [
    {
      text: title,
      options: {
        ...sidebarHeaderOptions,
        autoFit: true
      }
    },
    ...content
  ];
};

export const createPositions = (
  positions: { header: string; items: Position[] },
  internalExperiences: ProfileExport["internal_experiences"] = [],
  options: PptxgenJsTextOptions,
  formatDate: (str: string) => string
): TextItem[] => {
  if (!positions?.items || positions.items.length === 0) {
    return [];
  }

  const sortedInternalExperiences = sortInternalExperiences(internalExperiences);

  const orderedPositions = sortBy(positions.items, (pos) => -Number(pos.main));
  const positionsWithInternalExperiences = orderedPositions.map((item) => {
    const internalExperiencesForPosition = (sortedInternalExperiences || []).filter(
      ({ metadata }) => metadata.position_id === item.id
    );
    return {
      ...item,
      internalExperiences: internalExperiencesForPosition
    };
  });

  const output: TextItem[] = [];
  positionsWithInternalExperiences.forEach(
    ({ jobTitle, companyName, summary, internalExperiences, startDate, endDate }) => {
      output.push({
        text: companyName || defaultCompanyName,
        options: {
          bold: true,
          softBreakBefore: true
        }
      });
      output.push({
        text: formatExperienceText(formatDate, jobTitle, startDate, endDate), // pptxgenjs is hiding bullet when empty string
        options: {
          bold: true,
          softBreakBefore: true
        }
      });
      if (summary) {
        const summaryTexts = summary.replace(/\r/g, "").split(/\n/);
        summaryTexts.forEach((text) =>
          output.push({
            text,
            options: {
              softBreakBefore: true
            }
          })
        );
      }

      (internalExperiences || []).map(({ name, description, metadata: { date_range } }) => {
        output.push({
          // pptxgenjs does not allow to position bullet points indented,
          // puts it always far left, this is a workaround
          text: `• ${formatExperienceText(formatDate, name, date_range?.start_date, date_range?.end_date)}`,
          options: {
            bold: true,
            softBreakBefore: true
          }
        });
        if (description) {
          const texts = description
            .replace(/\r/g, "")
            .split(/\n/)
            // pptxgenjs does not allow to add extra indentation here, this is a workaround
            .map((text) => `   ${text}`);

          texts.forEach((text) =>
            output.push({
              text,
              options: {
                softBreakBefore: true,
                //passed for getLinesCount to output correct result
                indent: internalExperienceDescriptionIndent
              }
            })
          );
        }
      });
    }
  );

  return limitLines(output, options);
};

export const createSummary = (summary: string, options: PptxgenJsTextOptions): TextItem[] => {
  const summaryArray = limitText(summary, options);

  return summaryArray.map((row, index) => ({
    text: row.text,
    options: {
      paraSpaceBefore: 0,
      softBreakBefore: true,
      breakLine: index === summaryArray.length - 1
    }
  }));
};

export const createContacts = (
  contactInformationItems: ContactInformationItem[],
  email: string
): TextItem[] => {
  const output: TextItem[] = [];
  [
    { text: email, options: textOptions },
    ...contactInformationItems.filter(({ type }) => /mobile_number/.test(type))
  ].map(({ text }) => {
    output.push({
      text,
      options: textOptions
    });
  });
  return output;
};

export const createSkills = (skillsType: CustomType, profile: ProfileExport, t): TextItem[] => {
  const coreSkills = filterDeselectedSkills(profile.skills?.filter(({ top }) => top) || [], skillsType)
    .map(({ text }) => text)
    .sort((str1, str2) => str1.toLowerCase().localeCompare(str2.toLowerCase()));

  const otherSkills = filterDeselectedSkills(profile.skills?.filter(({ top }) => !top) || [], skillsType)
    .slice(0, 9)
    .map(({ text }) => text)
    .sort((str1, str2) => str1.toLowerCase().localeCompare(str2.toLowerCase()));

  const coreSkillsContent: TextItem[] = coreSkills.map((item) => ({
    text: item,
    options: {
      breakLine: true,
      bullet: { indent: 9 }
    }
  }));

  const otherSkillsContent: TextItem[] = otherSkills.map((item) => ({
    text: item,
    options: {
      breakLine: true,
      bullet: { indent: 9 }
    }
  }));

  const output: TextItem[] = [];
  if (coreSkillsContent.length) {
    output.push(
      ...[
        EMPTY_LINE,
        {
          text: t("eyPptx.coreSkills"),
          options: {
            ...sidebarHeaderOptions,
            autoFit: true
          }
        },
        ...coreSkillsContent
      ]
    );
  }

  if (otherSkillsContent.length) {
    output.push(
      ...[
        EMPTY_LINE,
        {
          text: t("eyPptx.otherSkills"),
          options: {
            ...sidebarHeaderOptions,
            autoFit: true
          }
        },
        ...otherSkillsContent
      ]
    );
  }

  return output;
};

export const createSidePanelTextContent = (
  data: ProfileExportFormatted,
  config,
  customTypes: CustomType[],
  order: string[],
  t: Function,
  translationKey: string,
  leftSidePanelTextOptions: PptxgenJsTextOptions
) => {
  const { contactInformationItems, email, name, id, jobTitle, _originalData } = data;
  const { isBlindModeOn } = config;

  const skillsFieldNames = ["skills"];
  const otherFieldNames = order.filter(
    (name) =>
      !["summary", "experience", "sectors", ...EY_CERTIFICATES_FIELD_NAMES, ...skillsFieldNames].includes(
        name
      )
  );

  const skillsType = customTypes.find(({ type }) => type.name === skillsFieldNames[0]);

  const leftDetailsTextArray: TextItem[] = [
    {
      text: isBlindModeOn ? `Candidate ${profileHash(id)}` : name,
      options: { ...sidebarHeaderOptions, fontSize: 12, underline: undefined }
    },
    {
      text: jobTitle || "",
      options: leftSidePanelTextOptions
    }
  ];

  if (!isBlindModeOn) {
    leftDetailsTextArray.push(...createContacts(contactInformationItems, email));
  }

  const skillsFields = skillsType ? createSkills(skillsType, _originalData, t) : [];
  skillsFields.length && leftDetailsTextArray.push(...skillsFields);

  otherFieldNames.forEach((name) => {
    const field = createCustomField(
      customTypes,
      [name],
      t(`${translationKey}.${name}`, humanize(capitalize(name))),
      leftSidePanelTextOptions
    );
    if (field.length) {
      leftDetailsTextArray.push(EMPTY_LINE);
      leftDetailsTextArray.push(...field);
    }
  });

  const allSectors = getSectors(data);
  if (allSectors.length) {
    leftDetailsTextArray.push(EMPTY_LINE);
    leftDetailsTextArray.push({
      text: t(`${translationKey}.sectors`),
      options: {
        ...sidebarHeaderOptions,
        autoFit: true
      }
    });
    leftDetailsTextArray.push(
      ...allSectors.map((item) => ({
        text: String(item.value),
        options: {
          breakLine: true,
          bullet: { indent: 9 }
        }
      }))
    );
  }

  return limitLines(leftDetailsTextArray, { ...leftSidePanelTextOptions });
};
